import { resolve } from "path";
import {
  DEFAULT_WAKE_RETRY_AFTER_SEC,
  isWakeAppDeferredResult,
  type WakeAppDeferredResult,
  type WakeAppResult,
} from "@gencow/core";

import { describeDbConnectionError, isDbConnectionError } from "./server-db-connection-check.js";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;

type WarmPoolAssignment = {
  appName: string;
  port: number;
  dataDir: string;
  role: "active" | "staging";
};

type RestorableAppRow = {
  id: number;
  name: string;
  port: number;
  dataDir: string;
  status: string;
  hasBackend: boolean;
};

type WarmAssignmentRow = RestorableAppRow & {
  staticDeployedAt: Date | null;
};

type IdleAppRow = {
  id: number;
  name: string;
  port: number;
  dataDir: string;
};

type ProvisionerRunnerBridge = {
  isAlive(appName: string): Promise<boolean>;
  isHealthy(appName: string, port: number): Promise<boolean>;
  stop(appName: string): Promise<void>;
};

type ProvisionerAutoRecoveryBridge = {
  provisionApp(appName: string, port: number, dataDir: string, appDbId: number): Promise<void>;
  wakeApp(appName: string): Promise<WakeAppResult>;
  setOnCrashCallback(callback: (appName: string) => Promise<void>): void;
  setOnMemoryOomCallback?(
    callback: (
      appName: string,
      event: { code: number | null; signal?: string | null; source?: string },
    ) => Promise<{ blocked: boolean }>,
  ): void;
  setOnSleepCallback(callback: (appName: string) => Promise<void>): void;
  setOnWakeCallback(callback: (appName: string) => Promise<void>): void;
  initRunner(): Promise<void>;
  getRunner(): ProvisionerRunnerBridge;
  setAppPort(appName: string, port: number): void;
  listWarmPoolAssignedApps(): Promise<WarmPoolAssignment[]>;
  unloadWarmPoolApp(appName: string): Promise<void>;
  reapWarmPoolStale(): Promise<unknown>;
  ensureRbacRoles(): Promise<void>;
  setLastAccess(appName: string): void;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  syncCronJobs(appDbId: number, appName: string, dataDir: string): Promise<void>;
};

type AppsTableLike = {
  id: unknown;
  name: unknown;
  port: unknown;
  dataDir: unknown;
  status: unknown;
  hasBackend: unknown;
  staticDeployedAt: unknown;
  nodeId?: unknown;
  memoryOomCount?: unknown;
  memoryOomWindowStartedAt?: unknown;
  memoryOomLastAt?: unknown;
  statusReason?: unknown;
  statusMessage?: unknown;
  statusUpdatedAt?: unknown;
};

type AutoRecoveryDb = {
  select(selection: Record<string, unknown>): {
    from(table: unknown): {
      where(condition: unknown): Promise<unknown[]>;
    };
  };
  update(table: unknown): {
    set(values: Record<string, unknown>): {
      where(condition: unknown): Promise<unknown>;
    };
  };
};

type EqOperator = (left: unknown, right: unknown) => unknown;
type InArrayOperator = (left: unknown, right: unknown[]) => unknown;
type AndOperator = (...conditions: any[]) => unknown;

export class AutoRecoveryWakeDeferredError extends Error {
  readonly status: WakeAppDeferredResult["status"];
  readonly retryAfterSec: number;

  constructor(result: WakeAppDeferredResult) {
    super(`Auto-recovery wake deferred (${result.status})`);
    this.name = "AutoRecoveryWakeDeferredError";
    this.status = result.status;
    this.retryAfterSec = result.retryAfterSec || DEFAULT_WAKE_RETRY_AFTER_SEC;
  }
}

function isAutoRecoveryWakeDeferredError(error: unknown): error is AutoRecoveryWakeDeferredError {
  return error instanceof AutoRecoveryWakeDeferredError;
}

export function createCapacityAwareRestoreApp(params: {
  wakeApp(appName: string): Promise<WakeAppResult>;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
}): (appName: string, port: number, dataDir: string, appDbId: number) => Promise<void> {
  return async (appName, port, dataDir, appDbId) => {
    params.setAppMeta(appName, { port, dataDir, appDbId });
    const result = await params.wakeApp(appName);
    if (result.ok) return;
    if (isWakeAppDeferredResult(result)) {
      throw new AutoRecoveryWakeDeferredError(result);
    }
    throw new Error(result.error);
  };
}

const AUTO_RECOVERY_BATCH_SIZE = 10;
const AUTO_RECOVERY_BATCH_DELAY_MS = 1000;
const AUTO_RECOVERY_MAX_DB_RETRIES = 3;

function filterRehydratableWarmPoolAssignments(assignments: WarmPoolAssignment[]): WarmPoolAssignment[] {
  return assignments.filter((assignment) => assignment.role === "active");
}

export function getAutoRecoveryRetryDelay(attempt: number): number {
  return Math.pow(2, attempt) * 1000;
}

export function buildNodeScopedAppCondition(params: {
  baseCondition: unknown;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  andOp?: AndOperator;
  currentNodeId?: string | null;
}): unknown {
  const currentNodeId = params.currentNodeId?.trim();
  if (!currentNodeId || !params.appsTable.nodeId || !params.andOp) {
    return params.baseCondition;
  }
  return params.andOp(params.baseCondition, params.eqOp(params.appsTable.nodeId, currentNodeId));
}

export async function detectRuntimeHasBackend(params: {
  port: number;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}): Promise<boolean | null> {
  const fetchImpl = params.fetchImpl ?? fetch;
  const timeoutMs = params.timeoutMs ?? 2000;

  try {
    const res = await fetchImpl(`http://127.0.0.1:${params.port}/health/ready`, {
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { queries?: number; mutations?: number };
    const totalFns = (body.queries || 0) + (body.mutations || 0);
    return totalFns > 0;
  } catch {
    return null;
  }
}

async function setAppStatus(params: {
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  appName: string;
  status: "running" | "failed" | "idle" | "crashed";
}): Promise<void> {
  await params.db
    .update(params.appsTable)
    .set({ status: params.status })
    .where(params.eqOp(params.appsTable.name, params.appName));
}

function resolveMemoryOomPolicy(env: Record<string, string | undefined> = process.env) {
  const threshold = Number(env.GENCOW_APP_MEMORY_OOM_THRESHOLD);
  const windowMinutes = Number(env.GENCOW_APP_MEMORY_OOM_WINDOW_MINUTES);
  return {
    threshold: Number.isInteger(threshold) && threshold > 0 ? threshold : 5,
    windowMinutes: Number.isInteger(windowMinutes) && windowMinutes > 0 ? windowMinutes : 360,
  };
}

async function recordAutoRecoveryMemoryOom(params: {
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  appName: string;
  logger: LoggerLike;
}): Promise<{ blocked: boolean; count: number; threshold: number }> {
  const policy = resolveMemoryOomPolicy();
  const now = new Date();
  const rows = (await params.db
    .select({
      name: params.appsTable.name,
      status: params.appsTable.status,
      memoryOomCount: params.appsTable.memoryOomCount,
      memoryOomWindowStartedAt: params.appsTable.memoryOomWindowStartedAt,
    })
    .from(params.appsTable)
    .where(params.eqOp(params.appsTable.name, params.appName))) as Array<{
    status: string;
    memoryOomCount?: number | null;
    memoryOomWindowStartedAt?: Date | string | null;
  }>;
  const row = rows[0];
  if (!row) return { blocked: false, count: 0, threshold: policy.threshold };

  const startedAt = row.memoryOomWindowStartedAt ? new Date(row.memoryOomWindowStartedAt) : null;
  const inWindow =
    startedAt instanceof Date &&
    !Number.isNaN(startedAt.getTime()) &&
    now.getTime() - startedAt.getTime() <= policy.windowMinutes * 60 * 1000;
  const count = inWindow ? Number(row.memoryOomCount ?? 0) + 1 : 1;
  const windowStartedAt = inWindow ? startedAt : now;
  const blocked = count >= policy.threshold;

  const patch: Record<string, unknown> = {
    memoryOomCount: count,
    memoryOomWindowStartedAt: windowStartedAt,
    memoryOomLastAt: now,
    statusUpdatedAt: now,
  };
  if (blocked) {
    patch.status = "memory_exceeded";
    patch.statusReason = "memory_oom_threshold";
    patch.statusMessage = `앱이 최근 ${policy.windowMinutes / 60}시간 동안 메모리 제한 초과 ${policy.threshold}회에 도달해 자동 중지되었습니다.`;
  }

  await params.db
    .update(params.appsTable)
    .set(patch)
    .where(params.eqOp(params.appsTable.name, params.appName));
  if (blocked) {
    params.logger.warn(`[platform] ${params.appName}: memory_exceeded (${count}/${policy.threshold})`);
  }
  return { blocked, count, threshold: policy.threshold };
}

async function rehydrateWarmPoolAssignments(params: {
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  andOp?: AndOperator;
  inArrayOp: InArrayOperator;
  currentNodeId?: string | null;
  assignments: WarmPoolAssignment[];
  setAppPort(appName: string, port: number): void;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  markAccess(appName: string): void;
  unloadWarmPoolApp(appName: string): Promise<void>;
  fetchImpl?: typeof fetch;
  logger: LoggerLike;
}): Promise<Set<string>> {
  const warmAssignments = filterRehydratableWarmPoolAssignments(params.assignments);
  const rehydratedApps = new Set<string>();

  if (warmAssignments.length === 0) {
    return rehydratedApps;
  }

  const rows = (await params.db
    .select({
      id: params.appsTable.id,
      name: params.appsTable.name,
      port: params.appsTable.port,
      dataDir: params.appsTable.dataDir,
      status: params.appsTable.status,
      hasBackend: params.appsTable.hasBackend,
      staticDeployedAt: params.appsTable.staticDeployedAt,
    })
    .from(params.appsTable)
    .where(
      buildNodeScopedAppCondition({
        baseCondition: params.inArrayOp(
          params.appsTable.name,
          warmAssignments.map((assignment) => assignment.appName),
        ),
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        andOp: params.andOp,
        currentNodeId: params.currentNodeId,
      }),
    )) as WarmAssignmentRow[];

  const rowMap = new Map<string, WarmAssignmentRow>(rows.map((row) => [row.name, row]));

  for (const assignment of warmAssignments) {
    const row = rowMap.get(assignment.appName);
    if (!row) {
      params.logger.warn(`[platform] warm-pool assignment without DB row: ${assignment.appName}`);
      continue;
    }

    if (!row.hasBackend) {
      params.logger.warn(
        `[platform] ${assignment.appName}: warm-pool assignment found for hasBackend=false app — unloading stale assignment`,
      );
      try {
        await params.unloadWarmPoolApp(assignment.appName);
      } catch (error) {
        params.logger.warn(
          `[platform] Failed to unload stale warm-pool assignment ${assignment.appName}:`,
          error,
        );
      }
      continue;
    }

    const runtimeHasBackend = await detectRuntimeHasBackend({
      port: assignment.port,
      fetchImpl: params.fetchImpl,
    });
    if (row.staticDeployedAt && runtimeHasBackend === false) {
      params.logger.warn(
        `[platform] ${assignment.appName}: warm-pool assignment has 0 functions despite hasBackend=true — reverting to static-only`,
      );
      try {
        await params.unloadWarmPoolApp(assignment.appName);
      } catch (error) {
        params.logger.warn(
          `[platform] Failed to unload static-only warm assignment ${assignment.appName}:`,
          error,
        );
      }
      await params.db
        .update(params.appsTable)
        .set({ hasBackend: false })
        .where(params.eqOp(params.appsTable.name, assignment.appName))
        .catch(() => {});
      continue;
    }

    params.setAppPort(assignment.appName, assignment.port);
    params.setAppMeta(assignment.appName, {
      port: assignment.port,
      dataDir: assignment.dataDir,
      appDbId: row.id,
    });
    params.markAccess(assignment.appName);
    rehydratedApps.add(assignment.appName);

    if (row.status === "failed") {
      await setAppStatus({
        db: params.db,
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        appName: assignment.appName,
        status: "running",
      });
    }
    params.logger.log(`[platform] warm-pool rehydrated: ${assignment.appName} on :${assignment.port}`);
  }

  return rehydratedApps;
}

async function restoreTargetApps(params: {
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  targetApps: RestorableAppRow[];
  restoreApp(appName: string, port: number, dataDir: string, appDbId: number): Promise<void>;
  runner: ProvisionerRunnerBridge;
  setAppPort(appName: string, port: number): void;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  markAccess(appName: string): void;
  delayImpl?: (ms: number) => Promise<void>;
  logger: LoggerLike;
}): Promise<void> {
  const delayImpl =
    params.delayImpl ?? ((ms: number) => new Promise((resolveDelay) => setTimeout(resolveDelay, ms)));

  if (params.targetApps.length === 0) {
    return;
  }

  params.logger.log(`[platform] Restoring ${params.targetApps.length} app(s) in batches of 10...`);
  let totalFailed = 0;
  let totalDeferred = 0;

  for (let i = 0; i < params.targetApps.length; i += AUTO_RECOVERY_BATCH_SIZE) {
    const batch = params.targetApps.slice(i, i + AUTO_RECOVERY_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map(async (row) => {
        if (!row.hasBackend) {
          if (await params.runner.isAlive(row.name)) {
            params.logger.warn(
              `[platform] ${row.name}: hasBackend=false but runner alive — stopping stray backend`,
            );
            await params.runner.stop(row.name);
          }
          params.logger.log(`[platform] ${row.name}: hasBackend=false — skipping auto-restore`);
          return;
        }

        if (row.status === "failed") {
          params.logger.log(`[platform] ${row.name}: failed (infra error) — auto-restoring`);
          if (await params.runner.isAlive(row.name)) {
            params.logger.log(
              `[platform] ${row.name}: failed but process still alive — killing before restore`,
            );
            await params.runner.stop(row.name);
          }
        } else if (await params.runner.isAlive(row.name)) {
          const healthy = await params.runner.isHealthy(row.name, row.port);
          if (healthy) {
            params.logger.log(`[platform] ✓ ${row.name} still alive and healthy — skipping`);
            params.setAppPort(row.name, row.port);
            params.setAppMeta(row.name, { port: row.port, dataDir: row.dataDir, appDbId: row.id });
            params.markAccess(row.name);
            return;
          }
          params.logger.log(`[platform] ${row.name} alive but unhealthy — restarting`);
        }

        await params.restoreApp(row.name, row.port, row.dataDir, row.id);
        if (row.status === "failed") {
          await setAppStatus({
            db: params.db,
            appsTable: params.appsTable,
            eqOp: params.eqOp,
            appName: row.name,
            status: "running",
          });
        }
        params.markAccess(row.name);
        params.logger.log(`[platform] ✓ Restored: ${row.name} on :${row.port}`);
      }),
    );

    const failed = results.filter((result) => result.status === "rejected");
    totalFailed += failed.length;

    for (const failedResult of failed) {
      const reason = (failedResult as PromiseRejectedResult).reason;
      const message = reason?.message || failedResult;
      if (isAutoRecoveryWakeDeferredError(reason)) {
        params.logger.warn(`[platform] Restore deferred: ${message}`);
      } else {
        params.logger.error(`[platform] ✗ Restore failed:`, message);
      }
    }

    if (failed.length > 0) {
      const failedIndices = results
        .map((result, index) => (result.status === "rejected" ? index : -1))
        .filter((index) => index >= 0);

      for (const index of failedIndices) {
        const failedApp = batch[index];
        if (!failedApp) continue;

        const rejected = results[index] as PromiseRejectedResult;
        const errorMessage = describeDbConnectionError(rejected.reason) || String(rejected.reason);

        if (isAutoRecoveryWakeDeferredError(rejected.reason)) {
          totalDeferred += 1;
          params.setAppMeta(failedApp.name, {
            port: failedApp.port,
            dataDir: failedApp.dataDir,
            appDbId: failedApp.id,
          });
          try {
            await setAppStatus({
              db: params.db,
              appsTable: params.appsTable,
              eqOp: params.eqOp,
              appName: failedApp.name,
              status: "idle",
            });
            params.logger.warn(`[platform] ${failedApp.name}: capacity-limited restore deferred → "idle"`);
          } catch {
            /* noop */
          }
        } else if (isDbConnectionError(rejected.reason)) {
          params.logger.warn(`[platform] ${failedApp.name}: DB connection error — retrying with backoff`);
          let retrySuccess = false;

          for (let attempt = 1; attempt <= AUTO_RECOVERY_MAX_DB_RETRIES; attempt++) {
            const delay = getAutoRecoveryRetryDelay(attempt);
            params.logger.log(
              `[platform] ${failedApp.name}: DB retry ${attempt}/${AUTO_RECOVERY_MAX_DB_RETRIES} in ${delay}ms...`,
            );
            await delayImpl(delay);

            try {
              await params.restoreApp(failedApp.name, failedApp.port, failedApp.dataDir, failedApp.id);
              if (failedApp.status === "failed") {
                await setAppStatus({
                  db: params.db,
                  appsTable: params.appsTable,
                  eqOp: params.eqOp,
                  appName: failedApp.name,
                  status: "running",
                });
              }
              params.markAccess(failedApp.name);
              retrySuccess = true;
              totalFailed -= 1;
              params.logger.log(`[platform] ${failedApp.name}: restore succeeded on retry ${attempt} ✓`);
              break;
            } catch (retryError: unknown) {
              const retryMessage = retryError instanceof Error ? retryError.message : String(retryError);
              params.logger.warn(
                `[platform] ${failedApp.name}: retry ${attempt}/${AUTO_RECOVERY_MAX_DB_RETRIES} failed: ${retryMessage.slice(0, 100)}`,
              );
            }
          }

          if (!retrySuccess) {
            params.logger.error(
              `[platform] ${failedApp.name}: all DB retry attempts exhausted — setting to "idle"`,
            );
            params.setAppMeta(failedApp.name, {
              port: failedApp.port,
              dataDir: failedApp.dataDir,
              appDbId: failedApp.id,
            });
            try {
              await setAppStatus({
                db: params.db,
                appsTable: params.appsTable,
                eqOp: params.eqOp,
                appName: failedApp.name,
                status: "idle",
              });
              params.logger.log(
                `[platform] ${failedApp.name}: DB connection error → "idle" (will auto-restore on next request)`,
              );
            } catch {
              /* noop */
            }
          }
        } else {
          try {
            await setAppStatus({
              db: params.db,
              appsTable: params.appsTable,
              eqOp: params.eqOp,
              appName: failedApp.name,
              status: "failed",
            });
            params.logger.log(
              `[platform] ${failedApp.name}: auto-recovery failed → DB status corrected to "failed"`,
            );
          } catch {
            /* noop */
          }
        }
      }
    }

    if (i + AUTO_RECOVERY_BATCH_SIZE < params.targetApps.length) {
      await delayImpl(AUTO_RECOVERY_BATCH_DELAY_MS);
    }
  }

  const restored = params.targetApps.length - totalFailed;
  params.logger.log(
    `[platform] Auto-recovery complete: ${restored}/${params.targetApps.length} restored, ${totalDeferred} deferred ✓`,
  );
}

async function registerIdleAppsForWake(params: {
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  andOp?: AndOperator;
  currentNodeId?: string | null;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  logger: LoggerLike;
}): Promise<IdleAppRow[]> {
  const idleApps = (await params.db
    .select({
      id: params.appsTable.id,
      name: params.appsTable.name,
      port: params.appsTable.port,
      dataDir: params.appsTable.dataDir,
    })
    .from(params.appsTable)
    .where(
      buildNodeScopedAppCondition({
        baseCondition: params.eqOp(params.appsTable.status, "idle"),
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        andOp: params.andOp,
        currentNodeId: params.currentNodeId,
      }),
    )) as IdleAppRow[];

  for (const row of idleApps) {
    params.setAppMeta(row.name, { port: row.port, dataDir: row.dataDir, appDbId: row.id });
  }

  if (idleApps.length > 0) {
    params.logger.log(`[platform] Registered ${idleApps.length} idle app(s) into memory for future wake ✓`);
  }

  return idleApps;
}

async function syncBootTimeCronJobs(params: {
  targetApps: RestorableAppRow[];
  idleApps: IdleAppRow[];
  syncCronJobs(appDbId: number, appName: string, dataDir: string): Promise<void>;
  logger: LoggerLike;
}): Promise<void> {
  const allAppsForCronSync = [...params.targetApps, ...params.idleApps];
  if (allAppsForCronSync.length === 0) {
    return;
  }

  let cronSyncCount = 0;
  for (const row of allAppsForCronSync) {
    try {
      await params.syncCronJobs(row.id, row.name, row.dataDir);
      cronSyncCount += 1;
    } catch (error: unknown) {
      params.logger.warn(
        `[cron] ${row.name}: boot-time cron sync failed:`,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  if (cronSyncCount > 0) {
    params.logger.log(
      `[platform] Boot-time cron sync complete for ${cronSyncCount}/${allAppsForCronSync.length} app(s) ✓`,
    );
  }
}

export async function bootstrapPlatformAutoRecovery(params: {
  functionsPath: string;
  db: AutoRecoveryDb;
  appsTable: AppsTableLike;
  eqOp: EqOperator;
  andOp?: AndOperator;
  inArrayOp: InArrayOperator;
  currentNodeId?: string | null;
  logger?: LoggerLike;
  fetchImpl?: typeof fetch;
  delayImpl?: (ms: number) => Promise<void>;
  importProvisioner?: (path: string) => Promise<ProvisionerAutoRecoveryBridge>;
}): Promise<void> {
  const logger = params.logger ?? console;
  const importProvisioner =
    params.importProvisioner ?? ((path: string) => import(path) as Promise<ProvisionerAutoRecoveryBridge>);

  const provisioner = await importProvisioner(resolve(params.functionsPath, "../src/provisioner.ts"));
  const {
    wakeApp,
    setOnCrashCallback,
    setOnMemoryOomCallback,
    setOnSleepCallback,
    setOnWakeCallback,
    initRunner,
    getRunner,
    setAppPort,
    listWarmPoolAssignedApps,
    unloadWarmPoolApp,
    reapWarmPoolStale,
    ensureRbacRoles,
    setLastAccess: markAccess,
    setAppMeta,
    syncCronJobs,
  } = provisioner;
  const restoreApp = createCapacityAwareRestoreApp({ wakeApp, setAppMeta });

  await initRunner();
  await ensureRbacRoles();

  setOnCrashCallback(async (appName: string) => {
    try {
      await setAppStatus({
        db: params.db,
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        appName,
        status: "crashed",
      });
      logger.log(`[platform] app "${appName}" marked as crashed in DB`);
    } catch (error: unknown) {
      logger.error(`[platform] failed to mark "${appName}" as crashed:`, error);
    }
  });

  setOnMemoryOomCallback?.(async (appName: string) => {
    const result = await recordAutoRecoveryMemoryOom({
      db: params.db,
      appsTable: params.appsTable,
      eqOp: params.eqOp,
      appName,
      logger,
    });
    return { blocked: result.blocked };
  });

  setOnSleepCallback(async (appName: string) => {
    try {
      await setAppStatus({
        db: params.db,
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        appName,
        status: "idle",
      });
      logger.log(`[platform] ${appName} DB status → idle ✓`);
    } catch (error: unknown) {
      logger.error(`[platform] Failed to set ${appName} to idle:`, error);
    }
  });

  setOnWakeCallback(async (appName: string) => {
    try {
      await setAppStatus({
        db: params.db,
        appsTable: params.appsTable,
        eqOp: params.eqOp,
        appName,
        status: "running",
      });
      logger.log(`[platform] ${appName} DB status → running ✓`);
    } catch (error: unknown) {
      logger.error(`[platform] Failed to set ${appName} to running:`, error);
    }
  });

  try {
    await reapWarmPoolStale();
  } catch (error: unknown) {
    logger.warn(
      `[platform] warm-pool stale reap failed on boot:`,
      error instanceof Error ? error.message : String(error),
    );
  }

  const rehydratedApps = await rehydrateWarmPoolAssignments({
    db: params.db,
    appsTable: params.appsTable,
    eqOp: params.eqOp,
    andOp: params.andOp,
    inArrayOp: params.inArrayOp,
    currentNodeId: params.currentNodeId,
    assignments: await listWarmPoolAssignedApps(),
    setAppPort,
    setAppMeta,
    markAccess,
    unloadWarmPoolApp,
    fetchImpl: params.fetchImpl,
    logger,
  });

  const targetApps = (
    (await params.db
      .select({
        id: params.appsTable.id,
        name: params.appsTable.name,
        port: params.appsTable.port,
        dataDir: params.appsTable.dataDir,
        status: params.appsTable.status,
        hasBackend: params.appsTable.hasBackend,
      })
      .from(params.appsTable)
      .where(
        buildNodeScopedAppCondition({
          baseCondition: params.inArrayOp(params.appsTable.status, ["running", "failed"]),
          appsTable: params.appsTable,
          eqOp: params.eqOp,
          andOp: params.andOp,
          currentNodeId: params.currentNodeId,
        }),
      )) as RestorableAppRow[]
  ).filter((row) => !rehydratedApps.has(row.name));

  await restoreTargetApps({
    db: params.db,
    appsTable: params.appsTable,
    eqOp: params.eqOp,
    targetApps,
    restoreApp,
    runner: getRunner(),
    setAppPort,
    setAppMeta,
    markAccess,
    delayImpl: params.delayImpl,
    logger,
  });

  const idleApps = await registerIdleAppsForWake({
    db: params.db,
    appsTable: params.appsTable,
    eqOp: params.eqOp,
    andOp: params.andOp,
    currentNodeId: params.currentNodeId,
    setAppMeta,
    logger,
  });

  await syncBootTimeCronJobs({
    targetApps,
    idleApps,
    syncCronJobs,
    logger,
  });
}
